import { useState, useEffect, useRef } from "react";
import {
  FileText, Trash2, Eye, Download, Plus, FileIcon,
  Calendar, User, Paperclip, CheckCircle2, Archive,
  AlertTriangle, X, Upload, Loader2, FileUp
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";

function formatBytes(bytes) {
  const n = parseInt(bytes, 10);
  if (isNaN(n)) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("id-ID", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Helper to load uploaded files from localStorage
function loadUploadedFiles() {
  try {
    return JSON.parse(localStorage.getItem("uploadedFiles") || "[]");
  } catch {
    return [];
  }
}

// Helper to save uploaded files to localStorage
function saveUploadedFiles(files) {
  localStorage.setItem("uploadedFiles", JSON.stringify(files));
}

export default function TelaahanHistory({ user, onOpenTelaah }) {
  const [uploadedFiles, setUploadedFiles] = useState(() => loadUploadedFiles());
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync state with localStorage whenever it changes externally
  useEffect(() => {
    const handleStorage = () => setUploadedFiles(loadUploadedFiles());
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Refresh from localStorage on mount (in case TelaahStaffModal added files)
  useEffect(() => {
    setUploadedFiles(loadUploadedFiles());
  }, []);

  // ---------- Upload File ----------
  const handleUploadFile = () => {
    if (!uploadFile) {
      toast.error("Pilih file terlebih dahulu");
      return;
    }
    if (uploadFile.size > 50 * 1024 * 1024) {
      toast.error("Ukuran file terlalu besar. Maksimal 50MB");
      return;
    }

    setUploading(true);

    // Konversi file ke base64 lalu simpan ke localStorage
    const reader = new FileReader();
    reader.onload = () => {
      const newFile = {
        id: Date.now().toString(),
        name: uploadFile.name,
        size: uploadFile.size,
        type: uploadFile.type,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.name || "Unknown",
        dataUrl: reader.result,
      };

      const updated = [newFile, ...uploadedFiles];
      setUploadedFiles(updated);
      saveUploadedFiles(updated);

      toast.success(`File "${uploadFile.name}" berhasil diunggah dan disimpan`);
      setUploadFile(null);
      setUploading(false);
      setUploadModalOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.onerror = () => {
      toast.error("Gagal membaca file");
      setUploading(false);
    };
    reader.readAsDataURL(uploadFile);
  };

  // ---------- Delete File ----------
  const handleDeleteFile = (fileId) => {
    if (!window.confirm("Hapus file ini dari daftar?")) return;
    const updated = uploadedFiles.filter((f) => f.id !== fileId);
    setUploadedFiles(updated);
    saveUploadedFiles(updated);
    toast.success("File berhasil dihapus");
  };

  // ---------- Empty State ----------
  if (uploadedFiles.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Telaahan Staff</h2>
            <p className="text-sm text-slate-500">Belum ada file yang diunggah</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setUploadModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <FileUp className="mr-2 h-4 w-4" />
              Unggah File
            </Button>
            <Button
              onClick={() => onOpenTelaah({ id: "new", nama: "Telaahan Baru", kategori: "Umum" })}
              className="bg-[#1e3a5f] hover:bg-[#152a46] text-white"
            >
              <Plus className="mr-2 h-4 w-4" />
              Telaahan Baru
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <FileText className="h-8 w-8" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">Belum Ada File Telaahan</h3>
          <p className="mt-2 max-w-md mx-auto text-sm text-slate-500">
            Mulai dengan mengunggah file dokumen pengaduan atau hasil telaahan yang sudah jadi. File akan tampil sebagai card di halaman ini.
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Button
              onClick={() => setUploadModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <FileUp className="mr-2 h-4 w-4" />
              Unggah File
            </Button>
            <Button
              onClick={() => onOpenTelaah({ id: "new", nama: "Telaahan Baru", kategori: "Umum" })}
              className="bg-[#1e3a5f] hover:bg-[#152a46] text-white"
            >
              <Plus className="mr-2 h-4 w-4" />
              Buat Telaahan Baru
            </Button>
          </div>
        </div>

        {uploadModalOpen && renderUploadModal()}
      </div>
    );
  }

  // ---------- Upload Modal ----------
  function renderUploadModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-lg rounded-lg bg-white shadow-xl flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileUp className="h-5 w-5 text-emerald-600" />
                Unggah File Telaahan
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Unggah file hasil telaahan staff (PDF, DOC, dll). Maksimal 50MB.
              </p>
            </div>
            <button
              onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-6 py-5 space-y-5 overflow-y-auto">
            <div className="flex flex-col items-center">
              <label
                className={`flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed p-8 transition-colors w-full ${
                  uploadFile
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50/50"
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
                    <p className="text-sm font-medium text-slate-700">Menyimpan file...</p>
                  </>
                ) : uploadFile ? (
                  <>
                    <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                    <p className="text-sm font-semibold text-slate-800 truncate max-w-full px-2">
                      {uploadFile.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUploadFile(null); }}
                      className="mt-1 text-xs text-red-500 hover:text-red-700 underline"
                    >
                      Hapus pilihan
                    </button>
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-slate-400" />
                    <p className="text-sm font-medium text-slate-700">
                      Klik atau seret file ke sini
                    </p>
                    <p className="text-[11px] text-slate-400">
                      Format: PDF, DOC, DOCX, JPG, PNG • Maksimal 50MB
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) setUploadFile(f);
                  }}
                  className="hidden"
                />
              </label>
            </div>
            <Button
              onClick={handleUploadFile}
              disabled={!uploadFile || uploading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Unggah & Simpan
                </>
              )}
            </Button>
          </div>
          <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
            >
              Tutup
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Main Render: Cards ----------
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Telaahan Staff</h2>
          <p className="text-sm text-slate-500">
            {uploadedFiles.length} file tersimpan
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setUploadModalOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <FileUp className="mr-2 h-4 w-4" />
            Unggah File
          </Button>
          <Button
            onClick={() => onOpenTelaah({ id: "new", nama: "Telaahan Baru", kategori: "Umum" })}
            className="bg-[#1e3a5f] hover:bg-[#152a46] text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Telaahan Baru
          </Button>
        </div>
      </div>

      {/* File Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {uploadedFiles.map((file) => (
          <div
            key={file.id}
            className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col"
          >
            {/* Card Top */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-red-500 shrink-0" />
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-blue-100 text-blue-700 border-blue-200">
                    <Upload className="h-3 w-3" />
                    {file.type?.includes("pdf") ? "PDF" : file.type?.includes("doc") ? "DOC" : "File"}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 mt-1">
                  {file.name}
                </h3>
              </div>
            </div>

            {/* Card Meta */}
            <div className="space-y-1.5 mb-4 flex-1">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar className="h-3 w-3 shrink-0" />
                <span>{formatDate(file.uploadedAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Paperclip className="h-3 w-3 shrink-0" />
                <span>{formatBytes(file.size)}</span>
              </div>
              {file.uploadedBy && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <User className="h-3 w-3 shrink-0" />
                  <span>{file.uploadedBy}</span>
                </div>
              )}
            </div>

            {/* Card Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
              {file.dataUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => {
                    const win = window.open();
                    if (win) {
                      if (file.type?.includes("pdf")) {
                        win.document.write(`<iframe src="${file.dataUrl}" style="width:100%;height:100%;border:none;" />`);
                      } else if (file.type?.includes("image")) {
                        win.document.write(`<img src="${file.dataUrl}" style="max-width:100%;height:auto;" />`);
                      } else {
                        win.document.write(`<p>Preview tidak tersedia untuk tipe file ini. Silakan unduh file.</p>`);
                      }
                    }
                  }}
                >
                  <Eye className="mr-1 h-3 w-3" />
                  View
                </Button>
              )}
              {file.dataUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = file.dataUrl;
                    link.download = file.name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <Download className="mr-1 h-3 w-3" />
                  Unduh
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => handleDeleteFile(file.id)}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Hapus
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Upload Modal */}
      {uploadModalOpen && renderUploadModal()}
    </div>
  );
}
